import React from 'react'
import { Stack, Typography, Box, Select, MenuItem, FormControl } from '@mui/material'
import { useTheme as useMuiTheme } from '@mui/material/styles'
import { useTheme } from '../../context/ThemeContext'

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

const YEARS = Array.from({ length: 2025 - 1950 + 1 }, (_, i) => 2025 - i)

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 48 * 4.5 + 8,
    },
  },
}

const DateSelectors = ({ fromDate, untilDate, onFromDateChange, onUntilDateChange, theme: propTheme }) => {
  const { isDarkMode } = useTheme()
  const muiTheme = useMuiTheme()
  // Use propTheme if provided, otherwise use muiTheme
  const theme = propTheme || muiTheme

  const handleDateChange = (type, field, value) => {
    const currentDate = type === 'from' ? fromDate : untilDate
    const [currentMonth, currentYear] = currentDate.split('/').map((s) => s.trim())

    let newDate
    if (field === 'month') {
      newDate = `${value}/${currentYear || ''}`
    } else {
      newDate = `${currentMonth || ''}/${value}`
    }

    if (type === 'from') {
      onFromDateChange(newDate.trim())
    } else {
      onUntilDateChange(newDate.trim())
    }
  }

  const getDateParts = (dateString) => {
    const [month, year] = dateString.split('/').map((s) => s.trim())
    return { month, year }
  }

  // Common select styling
  const selectStyle = {
    '& .MuiOutlinedInput-root': {
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.mode === 'dark' ? 'secondary.dark' : 'secondary.light',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.mode === 'dark' ? 'secondary.dark' : 'secondary.light',
      },
    },
    '& .MuiSelect-select': {
      fontSize: { xs: '0.9rem', sm: '1rem' },
    },
  }

  const renderDateSelector = (type, date, label) => {
    const { month, year } = getDateParts(date)

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          width: { xs: '100%', sm: 'auto' },
        }}
      >
        <Typography
          variant='body2'
          sx={{
            minWidth: 30,
            color: theme.palette.mode === 'dark' ? 'text.secondary' : 'text.secondary',
            fontWeight: 500,
          }}
        >
          {label}
        </Typography>
        <Stack
          direction='row'
          spacing={1}
          sx={{
            flex: 1,
          }}
        >
          <FormControl size='small' sx={{ minWidth: 110 }}>
            <Select
              value={month || ''}
              displayEmpty
              onChange={(e) => handleDateChange(type, 'month', e.target.value)}
              MenuProps={MenuProps}
              sx={selectStyle}
            >
              <MenuItem disabled value=''>
                <em>Month</em>
              </MenuItem>
              {MONTHS.map((month) => (
                <MenuItem
                  key={month}
                  value={month}
                  sx={{
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  }}
                >
                  {month}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size='small' sx={{ minWidth: 80 }}>
            <Select
              value={year || ''}
              displayEmpty
              onChange={(e) => handleDateChange(type, 'year', e.target.value)}
              MenuProps={MenuProps}
              sx={selectStyle}
            >
              <MenuItem disabled value=''>
                <em>Year</em>
              </MenuItem>
              {YEARS.map((year) => (
                <MenuItem
                  key={year}
                  value={year}
                  sx={{
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  }}
                >
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Box>
    )
  }

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: 2, sm: 2.5 }}
      sx={{
        width: '100%',
        alignItems: { xs: 'stretch', sm: 'center' },
      }}
    >
      {renderDateSelector('from', fromDate, 'From')}
      {renderDateSelector('until', untilDate, 'Until')}
    </Stack>
  )
}

export default DateSelectors
